import abcoLogo from "assets/abco-logo.png";
import AddToCart from "components/item/AddToCart";
import ItemStatusBadge from "components/item/ItemStatusBadge";
import { Box, Button, Card, FormattedCurrency, HStack, Icon, Image, Link, VStack } from "components/ui";
import ItemBadge from "components/ui/ItemBadge";
import { H4, Typography } from "components/ui/Typography";
import { PlusIcon, ShoppingCart } from "lucide-react-native";
import React, { useState, useCallback, useEffect } from "react";
import { Platform, useWindowDimensions, ViewStyle } from "react-native";
import { Item } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { useSiteSearch } from "utils/hooks/useSiteSearch";
import { ItemAlternativeButton } from "./ItemAlternativeButton";
import ItemSlideSheet from "./ItemSlideSheet";
import Animated, { Easing, useAnimatedStyle, useSharedValue, withRepeat, withTiming, interpolate } from "react-native-reanimated";
import { LinearGradient } from "expo-linear-gradient";
import { reportAddSearchClick } from "utils/addSearchAnalytics";

type Props = {
	item: Item;
	isOnNavigation?: boolean;
	isOnSearch?: boolean;
};

const ImageSkeleton = () => {
	const animatedValue = useSharedValue(0);

	useEffect(() => {
		animatedValue.value = withRepeat(withTiming(1, { duration: 1000 }), -1, true);
	}, []);

	const animatedStyle = useAnimatedStyle(() => {
		return {
			opacity: interpolate(animatedValue.value, [0, 1], [0.3, 0.7]),
		};
	});

	return (
		<Box
			bg="$neutral200"
			borderRadius={20}
			style={{
				width: "100%",
				height: 280,
				justifyContent: "center",
				alignItems: "center",
			}}
		>
			<Animated.View style={animatedStyle}>
				<Icon as={ShoppingCart} color="$neutral400" size="xl" />
			</Animated.View>
		</Box>
	);
};

const ItemCard = React.memo(function ItemCard({ item, isOnNavigation = false, isOnSearch = false }: Props) {
	const tag = null;
	const isMobile = useIsMobile();
	const [isSheetOpen, setSheetOpen] = useState(false);
	const toggleSheet = () => setSheetOpen(!isSheetOpen);
	const { width } = useWindowDimensions();
	const [isHovered, setIsHovered] = useState(false);
	const { close, searchTerm } = useSiteSearch();
	// Regular image loading state
	const [imageLoaded, setImageLoaded] = useState(false);

	// Memoize the main handleClick callback to prevent recreation
	const handleClick = useCallback(() => {
		if (isOnSearch && searchTerm?.length > 0 && item?.slug) {
			reportAddSearchClick({
				keyword: searchTerm,
				docid: item.slug,
			});
		}
		if (isOnNavigation) {
			close();
		}
	}, [isOnSearch, searchTerm, item?.slug, isOnNavigation, close]);

	// Cleanup function for image and hover state
	useEffect(() => {
		return () => {
			setImageLoaded(false);
			setIsHovered(false);
		};
	}, []);

	if (isMobile) {
		return (
			<Card maxWidth={width - 60} flex={1} key={item.stockcode} w={"$full"}>
				<Box padding={6} bg="$basicwhite" borderRadius={20} position="relative">
					{/* {!imageLoaded && <ImageSkeleton />} */}
					{item.imageUrl != null ? (
						<Link
							href={`/${item.slug}`}
							onPress={handleClick}
							component={(hovered) => {
								return (
									<Image
										aspectRatio={1}
										w="$full"
										source={item.imageUrl && process.env.EXPO_PUBLIC_ABCO_API_CDNURL ? process.env.EXPO_PUBLIC_ABCO_API_CDNURL + item.imageUrl + "?width=600&quality=75&format=webp" : undefined}
										borderTopLeftRadius={isMobile ? 8 : 20}
										borderTopRightRadius={isMobile ? 8 : 20}
										alignSelf="center"
										style={{
											width: "80%",
											height: "100%",
											maxHeight: 280,
											objectFit: "cover",
											minHeight: 280,
											transform: isHovered || hovered ? "scale(1.05)" : "scale(1)",
											// @ts-ignore
											transition: "transform 0.3s ease",
										}}
										alt=""
										resizeMode="contain"
										onLoad={() => setImageLoaded(true)}
									/>
								);
							}}
						>
							{/* <Image
								w="$full"
								source={item.imageUrl && process.env.EXPO_PUBLIC_ABCO_API_CDNURL ? process.env.EXPO_PUBLIC_ABCO_API_CDNURL + item.imageUrl + "?width=600&quality=75&format=webp" : undefined}
								borderTopLeftRadius={isMobile ? 8 : 20}
								borderTopRightRadius={isMobile ? 8 : 20}
								style={{
									width: "100%",
									height: "100%",
									objectFit: "cover",
									// transform: isHovered || hovered ? "scale(1.05)" : "scale(1)",
									// @ts-ignore
									transition: "transform 0.3s ease",
								}}
								alt=""
								resizeMode="contain"
								onLoad={() => setImageLoaded(true)}
							/> */}
						</Link>
					) : (
						<Link
							onPress={handleClick}
							component={(hovered) => {
								return (
									<Image
										aspectRatio={1}
										w="$full"
										source={abcoLogo}
										borderTopLeftRadius={isMobile ? 8 : 20}
										borderTopRightRadius={isMobile ? 8 : 20}
										alignSelf="center"
										// @ts-ignore
										style={{
											width: "80%",
											height: "100%",
											maxHeight: 280,
											objectFit: "cover",
											minHeight: 280,
											opacity: 0.3,
											filter: "grayscale(100%)",
											transform: isHovered || hovered ? "scale(1.05)" : "scale(1)",
											transition: "transform 0.3s ease",
										}}
										alt=""
										resizeMode="contain"
										onLoad={() => setImageLoaded(true)}
									/>
								);
							}}
							href={`/${item.slug}`}
						/>
					)}
					<Button
						onPress={() => toggleSheet()}
						zIndex={99}
						position="absolute"
						top={0}
						right={0}
						borderTopLeftRadius={0}
						borderBottomRightRadius={0}
						borderRadius={8}
						bg={"$secondary"}
						gap={6}
						paddingHorizontal={16}
						paddingVertical={8}
					>
						<Icon as={PlusIcon} color="$basicwhite" sx={{ fontSize: 16 }} />
						<Icon as={ShoppingCart} color="$basicwhite" sx={{ fontSize: 16 }} />
					</Button>
				</Box>

				<Box paddingVertical={20}>
					{item.stockcode && <ItemBadge stockcode={item.stockcode} />}
					<Link href={`/${item.slug}`} onPress={handleClick}>
						<H4 weight="bold" sx={{ paddingVertical: 8 }}>
							{item.name}
						</H4>
					</Link>
					<VStack width="100%" gap={8}>
						<Box>
							<FormattedCurrency
								sx={{
									fontSize: 18,
									lineHeight: 18,
									fontWeight: "bold",
									paddingVertical: 8,
									"@md": {
										fontSize: "$h3desktop",
										lineHeight: 40,
									},
								}}
								amount={item.unitPrice}
							/>
							<Box>
								<ItemStatusBadge item={item} />
							</Box>
						</Box>
						<ItemAlternativeButton item={item} />
					</VStack>
				</Box>

				<ItemSlideSheet item={item} isSheetOpen={isSheetOpen} toggleSheet={toggleSheet} />
			</Card>
		);
	}
	return (
		<Card key={item.stockcode} h={isMobile ? "auto" : "$full"} w={"$full"} display="flex" flexDirection="column">
			<Box bg="$basicwhite" borderRadius={20} alignItems="center">
				<Box w={"$full"} h={"$full"} maxHeight={280} overflow="hidden">
					{!imageLoaded && <ImageSkeleton />}
					{item.imageUrl != null ? (
						<Link
							onPress={handleClick}
							component={(hovered) => {
								return (
									<Image
										aspectRatio={1}
										w="$full"
										source={item.imageUrl && process.env.EXPO_PUBLIC_ABCO_API_CDNURL ? process.env.EXPO_PUBLIC_ABCO_API_CDNURL + item.imageUrl + "?width=600&quality=75&format=webp" : undefined}
										borderTopLeftRadius={isMobile ? 8 : 20}
										borderTopRightRadius={isMobile ? 8 : 20}
										style={{
											width: "100%",
											height: "100%",
											maxHeight: 280,
											objectFit: "cover",
											transform: isHovered || hovered ? "scale(1.05)" : "scale(1)",
											// @ts-ignore
											transition: "transform 0.3s ease",
										}}
										alt=""
										resizeMode="contain"
										onLoad={() => setImageLoaded(true)}
									/>
								);
							}}
							href={`/${item.slug}`}
						/>
					) : (
						<Link
							onPress={handleClick}
							component={(hovered) => {
								return (
									<Image
										aspectRatio={1}
										w="$full"
										source={abcoLogo}
										borderTopLeftRadius={isMobile ? 8 : 20}
										borderTopRightRadius={isMobile ? 8 : 20}
										alignSelf="center"
										// @ts-ignore
										style={{
											width: "80%",
											height: "100%",
											maxHeight: 280,
											objectFit: "cover",
											minHeight: 280,
											opacity: 0.3,
											filter: "grayscale(100%)",
											transform: isHovered || hovered ? "scale(1.05)" : "scale(1)",
											transition: "transform 0.3s ease",
										}}
										alt=""
										resizeMode="contain"
										onLoad={() => setImageLoaded(true)}
									/>
								);
							}}
							href={`/${item.slug}`}
						/>
					)}
				</Box>
			</Box>
			<Box flex={1} display="flex" flexDirection="column">
				<Box paddingTop={20} flex={1}>
					{item.stockcode && <ItemBadge stockcode={item.stockcode} />}
					<Link
						onPress={handleClick}
						href={`/${item.slug}`}
						onHoverIn={() => {
							setIsHovered(true);
						}}
						onHoverOut={() => {
							setIsHovered(false);
						}}
					>
						{/* <H4 weight="bold" sx={{ paddingVertical: 8 }}>
							{item.name}
						</H4> */}
						{Platform.OS === "web" ? <h4 className="item-list-base-title">{item.name ? item.name : "No Item name detected"}</h4> : <Typography.H4 weight="bold">{item.name ? item.name : "No Item name detected"}</Typography.H4>}
					</Link>
				</Box>
				<HStack alignItems="center" width={"100%"} gap={8}>
					<FormattedCurrency
						sx={{
							fontSize: 18,
							lineHeight: 18,
							fontWeight: "bold",
							paddingVertical: 8,
							"@md": {
								fontSize: "$h3desktop",
								lineHeight: 40,
							},
						}}
						amount={item.unitPrice}
					/>
					<ItemStatusBadge item={item} />
				</HStack>
				<ItemAlternativeButton item={item} />
				{/* Footer with add to cart button */}
				<HStack marginTop="auto" justifyContent="space-between" alignItems="center" width="100%" paddingTop={12}>
					<AddToCart stockcode={item.stockcode} isPurchaseable={item.isPurchaseable} item={item} isOnSearch={isOnSearch} />
				</HStack>
				<ItemSlideSheet item={item} isSheetOpen={isSheetOpen} toggleSheet={toggleSheet} />
			</Box>
		</Card>
	);
});

export default ItemCard;
